import { useCallback, useRef, useState } from 'react';
import { geoBounds } from 'd3-geo';

type AutoFitParams = {
  height: number;
  padding?: number; // padding in pixels around the map
};

export function useAutoFitGeoJson({ height, padding = 20 }: AutoFitParams) {
  const [center, setCenter] = useState<[number, number]>([-74.5, 4.0]);
  const [scale, setScale] = useState<number>(1000);
  const appliedRef = useRef<boolean>(false);

  const fitGeographies = useCallback(
    (geographies: any[]) => {
      // Only apply once
      if (appliedRef.current) return;
      if (!geographies || geographies.length === 0) return;

      // Calculate combined bounds of all geographies
      let combinedBounds: [[number, number], [number, number]] | null = null;

      geographies.forEach((geo: any) => {
        try {
          const b = geoBounds(geo);
          if (
            !isFinite(b[0][0]) ||
            !isFinite(b[0][1]) ||
            !isFinite(b[1][0]) ||
            !isFinite(b[1][1])
          ) {
            return;
          }
          if (!combinedBounds) {
            combinedBounds = b as [[number, number], [number, number]];
          } else {
            combinedBounds = [
              [Math.min(combinedBounds[0][0], b[0][0]), Math.min(combinedBounds[0][1], b[0][1])],
              [Math.max(combinedBounds[1][0], b[1][0]), Math.max(combinedBounds[1][1], b[1][1])],
            ];
          }
        } catch (e) {
          // Skip invalid geometries
        }
      });

      if (!combinedBounds) return;

      const bounds = combinedBounds as [[number, number], [number, number]];
      const [[minLon, minLat], [maxLon, maxLat]] = bounds;

      // Calculate center
      const centerLon = (minLon + maxLon) / 2;
      const centerLat = (minLat + maxLat) / 2;

      // Calculate the appropriate scale to fit the bounds based on height only
      const latDelta = Math.abs(maxLat - minLat);

      // Available space after padding (based on height)
      const availableHeight = height - padding * 2;

      // For Mercator projection, calculate scale based on latitude extent
      const scaleY = availableHeight / latDelta;

      // Mercator projection scale factor (approximate)
      // This converts our pixel-per-degree to the d3 Mercator scale
      const mercatorScale = scaleY * 55; // Adjusted factor for better fit

      setCenter([centerLon, centerLat]);
      setScale(mercatorScale);
      appliedRef.current = true;
    },
    [height, padding]
  );

  const resetFit = useCallback(() => {
    appliedRef.current = false;
  }, []);

  return {
    initialCenter: center,
    initialScale: scale,
    fitGeographies,
    resetFit,
  };
}

export default useAutoFitGeoJson;
